/**
 * "Manage teachers" (the Unity app's ManageTeachers / EditingTeacher panels),
 * for a school licence's manager: the school's name, its seats and expiry,
 * and the teachers, added by e-mail (they are Premium once they sign in with
 * it) and removed (their seat is free again). The manager does not take a seat.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { addTeacher, fetchSchool, removeTeacher, renameSchool, SchoolError, type ManagedSchool, type SchoolTeacher } from "../data/school";
import { currentLanguage, t } from "../player/i18n";
import { LINKS } from "./Landing";
import { schoolErrorText, st } from "./schoolText";
import { ConfirmDialog, Icon, Modal, PromptDialog } from "./widgets";

export function SchoolView() {
  const [school, setSchool] = useState<ManagedSchool | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<"add" | "rename" | null>(null);
  const [removing, setRemoving] = useState<SchoolTeacher | null>(null);
  const lang = currentLanguage();
  const dateFormat = useMemo(() => new Intl.DateTimeFormat(lang, { dateStyle: "long" }), [lang]);

  useEffect(() => {
    fetchSchool()
      .then(setSchool)
      .catch(() => setError(t("errGeneric")));
  }, []);

  if (!school) {
    return (
      <section className="view">
        <h1>{st("manage")}</h1>
        <p className={error ? "error" : "muted"}>{error ?? "…"}</p>
      </section>
    );
  }

  const date = dateFormat.format(school.expiresAt);
  const expired = school.expiresAt <= Date.now();
  const full = school.teachers.length >= school.seats;
  return (
    <section className="view school-view">
      <header className="view-head">
        <h1>{st("manage")}</h1>
        <button className="btn btn-small" onClick={() => setDialog("add")} disabled={full}>
          {st("add")}
        </button>
      </header>
      <p className="muted">{st("intro", { seats: school.seats })}</p>
      <div className="panel-list">
        <button className="panel-row" onClick={() => setDialog("rename")} title={st("rename")}>
          <span>{st("schoolName")}</span>
          <span className="muted row-value">
            <span dir="auto">{school.name || "—"}</span>
            <Icon name="rename" size={18} />
          </span>
        </button>
        <div className="panel-row">
          <span>{st("places", { used: school.teachers.length, seats: school.seats })}</span>
          <span className={expired ? "error row-value" : "muted row-value"}>{expired ? st("expired", { date, email: LINKS.email }) : st("validUntil", { date })}</span>
        </div>
      </div>
      {school.teachers.length === 0 ? (
        <p className="muted">{st("none")}</p>
      ) : (
        <div className="panel-list school-teachers">
          {school.teachers.map((teacher) => (
            <div key={teacher.email} className="panel-row school-teacher">
              <span className="school-teacher-who">
                <span dir="auto">{teacher.name || teacher.email}</span>
                {teacher.name && <span className="muted small">{teacher.email}</span>}
              </span>
              <span className="row-value">
                <span className={teacher.hasAccount ? "school-joined small" : "muted small"}>{teacher.hasAccount ? st("signedUp") : st("notYet")}</span>
                <button className="link-btn" onClick={() => setRemoving(teacher)}>
                  {st("remove")}
                </button>
              </span>
            </div>
          ))}
        </div>
      )}
      {full && <p className="muted small">{st("more", { email: LINKS.email })}</p>}

      {dialog === "add" && <AddTeacherDialog onAdded={setSchool} onClose={() => setDialog(null)} />}
      {dialog === "rename" && (
        <PromptDialog title={st("rename")} label={st("schoolName")} initial={school.name} onSubmit={async (name) => setSchool(await renameSchool(name))} onClose={() => setDialog(null)} />
      )}
      {removing && (
        <ConfirmDialog
          title={st("removeTitle", { email: removing.email })}
          text={st("removeText")}
          confirmLabel={st("remove")}
          danger
          onConfirm={async () => setSchool(await removeTeacher(removing.email))}
          onClose={() => setRemoving(null)}
        />
      )}
    </section>
  );
}

/** EditingTeacher.CreateNew: the teacher's e-mail and name (no password: they sign up themselves). */
function AddTeacherDialog({ onAdded, onClose }: { onAdded: (school: ManagedSchool) => void; onClose: () => void }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      onAdded(await addTeacher(email.trim(), name.trim()));
      onClose();
    } catch (err) {
      setError((err instanceof SchoolError && schoolErrorText(err.message)) || t("errGeneric"));
      setBusy(false);
    }
  };
  return (
    <Modal title={st("add")} onClose={onClose}>
      <form onSubmit={submit} className="dialog-form">
        <input className="text-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={st("email")} aria-label={st("email")} autoFocus maxLength={254} />
        <input dir="auto" className="text-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={st("nameOpt")} aria-label={st("nameOpt")} maxLength={60} />
        {error && <p className="error">{error}</p>}
        <div className="dialog-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t("cancel")}
          </button>
          <button type="submit" className="btn" disabled={!email.trim() || busy}>
            {busy ? "…" : st("addBtn")}
          </button>
        </div>
      </form>
    </Modal>
  );
}
